import React, { useState, useEffect } from "react";
import useStatePromise from "use-state-promise";

import Box from "@mui/material/Box";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Alert, { AlertColor } from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";

import GeneralSettings from "./tabs/GeneralSettings";
import OrderNotiSettings from "./tabs/OrderNotiSettings";
import PermissionSettings from "./tabs/PermissionSettings";
import StoreSettings from "./tabs/StoreSettings";

import { updateAsSmsSettings, fetchAsSmsSettings } from "./utils/api";

interface TabPanelProps {
	children?: React.ReactNode;
	index: number;
	value: number;
}

const TabPanel = ({ children, index, value }: TabPanelProps) => (
	<div
		role='tabpanel'
		hidden={value !== index}
		id={`settings-screen-panel-${index}`}>
		{value === index && <Box sx={{ p: 3 }}>{children}</Box>}
	</div>
);

const a11yProps = (index: number) => {
	return {
		id: `settings-screen-panel-${index}`,
		"aria-controls": `settings-screen-panel-${index}`,
	};
};

const Settings = () => {
	const [value, setValue] = useState(0);
	const [settings, setSettingsPromise, setSettings] = useStatePromise<
		Record<string, string | boolean | string[]>
	>({});
	const [loading, setLoading] = useState(false);
	const [showNotice, setShowNotice] = useState(false);
	const [notice, setNotice] = useState<{ type: AlertColor; message: string }>({
		type: "success",
		message: "",
	});

	useEffect(() => {
		fetchAsSmsSettings().then((res) => {
			setSettings(res.data);
		});
	}, []);

	const handleUpdateSettings = (
		settings: Record<string, string | boolean | string[]>,
	) => {
		updateAsSmsSettings(settings).then((res) => {
			if (res.status === 200) {
				setNotice({
					...notice,
					message: "Settings saved",
				});
			} else {
				setNotice({
					type: "error",
					message: "Settings save failed",
				});
			}
			setShowNotice(true);
			setLoading(false);
		});
	};

	const handleSubmit = (e: any) => {
		e.preventDefault();
		setLoading(true);
		handleUpdateSettings(settings);
	};

	const handlePanelChange = (e: React.SyntheticEvent, newValue: number) => {
		setValue(newValue);
	};

	return (
		<>
			{settings["enable-test-mode"] === "true" && (
				<Alert severity='warning' sx={{ my: 2 }}>
					<AlertTitle>
						<b>Important</b>
					</AlertTitle>
					MessageFlow is currently in test mode. A real SMS message will not be
					sent when test mode is on.{" "}
					<a href='https://messageflow.co' target='_blank' rel='noreferrer'>
						Read More
					</a>
				</Alert>
			)}
			<Box sx={{ padding: "20px" }}>
				<Tabs
					value={value}
					onChange={handlePanelChange}
					aria-label='basic tabs example'>
					<Tab label='General' {...a11yProps(0)} />
					<Tab label='Store Settings' {...a11yProps(1)} />
					<Tab label='Order Notification' {...a11yProps(2)} />
					<Tab label='Permissions' {...a11yProps(3)} />
				</Tabs>
			</Box>
			<TabPanel value={value} index={0}>
				<GeneralSettings
					handleSubmit={handleSubmit}
					settings={settings}
					setSettings={setSettings}
					setSettingsPromise={setSettingsPromise}
					notice={notice}
					showNotice={showNotice}
					setShowNotice={setShowNotice}
					loading={loading}
					handleUpdateSettings={handleUpdateSettings}
				/>
			</TabPanel>
			<TabPanel value={value} index={1}>
				<StoreSettings
					loading={loading}
					handleSubmit={handleSubmit}
					notice={notice}
					showNotice={showNotice}
					setShowNotice={setShowNotice}
					settings={settings}
					setSettingsPromise={setSettingsPromise}
					setSettings={setSettings}
					handleUpdateSettings={handleUpdateSettings}
				/>
			</TabPanel>
			<TabPanel value={value} index={2}>
				<OrderNotiSettings
					handleSubmit={handleSubmit}
					settings={settings}
					setSettings={setSettings}
					notice={notice}
					showNotice={showNotice}
					setShowNotice={setShowNotice}
					loading={loading}
				/>
			</TabPanel>
			<TabPanel value={value} index={3}>
				<PermissionSettings
					handleSubmit={handleSubmit}
					settings={settings}
					setSettings={setSettings}
					notice={notice}
					showNotice={showNotice}
					setShowNotice={setShowNotice}
					loading={loading}
				/>
			</TabPanel>
		</>
	);
};

export default Settings;
